import { usePermissao } from '@/utils/permissao';
import { AnexosPanel } from '@/components/anexos/AnexosPanel';
import { EntityIcon } from "@/components/ui/cores";
import { PageHeader } from '@/components/ui/page-header';
import { FormActions } from '@/components/ui/form-actions';
import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useVoltar } from '@/utils/voltar';
import { ClienteService } from '../../services';
import { TipoPessoaEnum } from '@/types/enum/TipoPessoa';
import { toast } from 'sonner';
import { Row } from '@/components/grid/row';
import { Card } from '@/components/ui/card';
import { FormikInput } from '@/components/ui/input';
import { FormikToogle, ToggleGroupItem } from '@/components/ui/toggle-group';
import SeguradoTelefoneSection from './SeguradoTelefoneSection';
import { Segurado } from '@/types/Segurado';
import { TelefoneContatoDTO } from '@/types/TelefoneContatoDTO';
import { EmailContatoDTO } from '@/types/EmailContatoDTO';
import SeguradoEmailSection from './SeguradoEmailSection';
import SeguradoEnderecoSection from './SeguradoEnderecoSection';
import { Endereco } from '@/types/Endereco';
import { getTenant } from '@/utils/TenantUtils';

export const clienteFormValidationSchema = Yup.object().shape({
    tipoPessoa: Yup.string().required('Tipo é obrigatório'),
    nome: Yup.string().when('tipoPessoa', {
        is: TipoPessoaEnum.fisica,
        then: (schema) => schema.required('Nome é obrigatório'),
        otherwise: (schema) => schema.required('Razão Social é obrigatória'),
    }),
    telefones: Yup.array().of(
        Yup.object().shape({
          descricao: Yup.string().required("A descrição é obrigatória"),
          telefone: Yup.string().required("O telefone ou celular é obrigatório")
        })),
    emails: Yup.array().of(
        Yup.object().shape({
            descricao: Yup.string().required("A descrição é obrigatória"),
            email: Yup.string().required("O e-mail é obrigatório").email("O e-mail é inválido")
        })),
    enderecos: Yup.array().of(
        Yup.object().shape({
            cep: Yup.string().required("O cep é obrigatório"),
            logradouro: Yup.string().required("O logradouro é obrigatório"),
            numero: Yup.string().required("O número é obrigatório"),
            bairro: Yup.string().required("O bairro é obrigatório"),
            cidade: Yup.string().required("A cidade é obrigatória"),
            uf: Yup.string().required("A UF é obrigatória"),
        }))        
});

const initialValues: Segurado = {
    nome: '',
    tipoPessoa: TipoPessoaEnum.fisica,
    ativo: true,
    telefones: [],
    emails: [],
    enderecos: []
};

type SeguradoFormPageParams = {
    hideBreadcrumb?: boolean;
    hideSaveButton?: boolean;
    showInsidePanel?: boolean;
    disableStatus?: boolean;
    formikProps?: FormikProps<Segurado>;
}

const SeguradoFormPage: React.FC<SeguradoFormPageParams> = ({
    hideBreadcrumb = false, 
    hideSaveButton = false
}) => {
    const somenteLeitura = !usePermissao('SEGURADO_WRITE');
    const [cliente, setCliente] = useState<Segurado>(initialValues);
    const [loading, setLoading] = useState<boolean>(false);
    const [indexTelefoneEditando, setIndexTelefoneEditando] = useState<number|undefined>(undefined);
    const [indexEmailEditando, setIndexEmailEditando] = useState<number|undefined>(undefined);
    const [indexEnderecoEditando, setIndexEnderecoEditando] = useState<number|undefined>(undefined);
    const voltar = useVoltar();
    const {id: codigo} = useParams();

    const [loadingCep, setLoadingCep] = useState<boolean>(false);

    useEffect(() => {
        if (codigo && codigo !== 'novo') {
            setLoading(true);
            
            ClienteService.getByCodigo(codigo)
              .then(result => {
                  setCliente(result.data);
              })
              .catch(error => {
                  console.error(error);
              })
              .finally(() => setLoading(false));
        }        
    },[codigo]);

    const onSave = (clienteDTO: Segurado) => {
        setLoading(true);
        if (codigo && codigo !== 'novo'){
            ClienteService.update(clienteDTO, codigo)
            .then(result => {
                setCliente(result.data);
                toast.success('Segurado alterado com sucesso.');
                voltar(`/${getTenant()}/segurado`);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setLoading(false));
        } else {
            ClienteService.create(clienteDTO)
            .then(result => {
                setCliente(result.data);
                toast.success('Segurado cadastrado com sucesso.');
                voltar(`/${getTenant()}/segurado`);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setLoading(false));
        }        
    }

    const novoContatoTelefone = (formikProps: FormikProps<Segurado>) => {
        var telefones = formikProps.values.telefones;
        var novoTelefone: TelefoneContatoDTO = {
            descricao: '',
            telefone: ''
        };
        telefones!.push(novoTelefone);        
        formikProps.setFieldValue('telefones', telefones);
        setIndexTelefoneEditando(telefones!.length-1);
    }

    const removerContatoTelefone = (formikProps: FormikProps<Segurado>, indexRemover: number) => {
        var telefones = formikProps.values.telefones;
        telefones?.splice(indexRemover, 1);  
        formikProps.setFieldValue('telefones', telefones);
    }

    const novoContatoEmail = (formikProps: FormikProps<Segurado>) => {
        var emails = formikProps.values.emails;
        var novo: EmailContatoDTO = {
            descricao: '',
            email: ''
        };
        emails!.push(novo);        
        formikProps.setFieldValue('emails', emails);
        setIndexEmailEditando(emails!.length-1);
    }

    const removerContatoEmail = (formikProps: FormikProps<Segurado>, indexRemover: number) => {
        var emails = formikProps.values.emails;
        emails?.splice(indexRemover, 1);  
        formikProps.setFieldValue('emails', emails);
    }

    const novoEndereco = (formikProps: FormikProps<Segurado>) => {
        var enderecos = formikProps.values.enderecos;
        var novo: Endereco = {
            cep: '',
            logradouro: '',
            numero: '',
            bairro: '',
            complemento: '',
            cidade: '',
            uf: ''
        };
        enderecos!.push(novo);        
        formikProps.setFieldValue('enderecos', enderecos);
        setIndexEnderecoEditando(enderecos!.length-1);
    }

    const removerEndereco = (formikProps: FormikProps<Segurado>, indexRemover: number) => {
        var enderecos = formikProps.values.enderecos;
        enderecos?.splice(indexRemover, 1);  
        formikProps.setFieldValue('enderecos', enderecos);
    }

    return (
        <div className="space-y-6">
            <PageHeader icon={<EntityIcon entidade="segurado" />} title={somenteLeitura ? 'Segurado' : codigo && codigo !== 'novo' ? 'Editar segurado' : 'Novo segurado'} />
            <Card className='p-6 shadow-none'>
                <Formik initialValues={cliente} validationSchema={clienteFormValidationSchema} onSubmit={onSave} enableReinitialize>
                    {(formikProps: FormikProps<Segurado>) => (
                        <>
                        <Form>
                            <fieldset disabled={somenteLeitura} className="min-w-0">
                            <Row>
                                <FormikInput name='cpfCnpj' 
                                    label={formikProps.values.tipoPessoa === TipoPessoaEnum.fisica ? 'CPF' : 'CNPJ'} 
                                    size={3} />
                                <FormikInput name='rgIe' label={formikProps.values.tipoPessoa === TipoPessoaEnum.fisica ? 'RG' : 'Inscrição Estadual'} size={3} />
                                <FormikToogle size={3} label='Tipo' name='tipoPessoa'>
                                    <ToggleGroupItem size={"default"} value="FISICA">Física</ToggleGroupItem>
                                    <ToggleGroupItem value="JURIDICA">Jurídica</ToggleGroupItem>
                                </FormikToogle>
                            </Row>
                            <Row>
                                <FormikInput name='nome' label={formikProps.values.tipoPessoa === TipoPessoaEnum.fisica ? 'Nome' : 'Razão Social'} size={12} required />
                            </Row>
                            <Row>
                                <SeguradoTelefoneSection 
                                    formikProps={formikProps} 
                                    loading={loading} 
                                    indexTelefoneEditando={indexTelefoneEditando}
                                    novoContatoTelefone={() => novoContatoTelefone(formikProps)} 
                                    alterarContatoTelefone={(index) => setIndexTelefoneEditando(index)}
                                    removerContatoTelefone={(index) => removerContatoTelefone(formikProps, index)}
                                    salvarContatoTelefone={() => setIndexTelefoneEditando(undefined)} />
                            </Row>
                            <Row>
                                <SeguradoEmailSection 
                                    formikProps={formikProps} 
                                    loading={loading} 
                                    indexEditando={indexEmailEditando}
                                    novo={() => novoContatoEmail(formikProps)} 
                                    alterar={(index) => setIndexEmailEditando(index)}
                                    remover={(index) => removerContatoEmail(formikProps, index)}
                                    salvar={() => setIndexEmailEditando(undefined)} />
                            </Row>
                            <Row>
                                <SeguradoEnderecoSection 
                                    formikProps={formikProps} 
                                    loading={loading} 
                                    indexEditando={indexEnderecoEditando}
                                    novo={() => novoEndereco(formikProps)} 
                                    alterar={(index) => setIndexEnderecoEditando(index)}
                                    remover={(index) => removerEndereco(formikProps, index)}
                                    salvar={() => setIndexEnderecoEditando(undefined)} />
                            </Row>
                            </fieldset>

                            {!hideSaveButton && <AnexosPanel entidade="segurado" registro={codigo && codigo !== 'novo' ? codigo : undefined} embutido somenteLeitura={somenteLeitura} />}
                            {!hideSaveButton ? (
                                <FormActions loading={loading} onSave={() => formikProps.handleSubmit()} somenteLeitura={somenteLeitura} />
                            ):(<></>)}
                        </Form>
                        </>
                    )}                
                </Formik>
            </Card>            
        </div>
    );
}

export default SeguradoFormPage;